import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Typography, TextField, Checkbox } from '@ann/web-components';
import { observer } from 'mobx-react';
import { useStore } from '../../../../../../../hooks';
import { useToast } from '../../../../../../../components/toast';
import { useMediaQueries } from '../../../../../../../utils/mediaQueries';
import { ModalBodyContent, MessageText, FieldContainer, CheckboxContainer, CheckboxHitArea } from './ChangeOwnerModal.styled';

const ChangeOwnerModal = ({
  isOpen,
  onClose,
  onConfirm,
  workspace = null,
  isLoading = false
}) => {
  const { workspaceStore } = useStore();
  const toast = useToast();
  const { isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const previousLoadingRef = useRef(false);
  
  // Calculate modal width: 90% for iPad portrait, 900px otherwise
  const modalWidth = useMemo(() => {
    if (isIpadAirPortrait || isIpadProPortrait) {
      return '90%';
    }
    return '900px';
  }, [isIpadAirPortrait, isIpadProPortrait]);
  
  // Get current owner email from workspace with fallbacks
  const currentOwnerEmail = workspace?.contact_email || workspace?.contactEmail || workspace?.ownerEmail || workspace?.owner_email || '';
  
  const title = 'Change Workspace Owner';
  const message = 'This action transfers full ownership and administrative permissions to another user.';

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = newOwnerEmail.trim().length > 0 && emailRegex.test(newOwnerEmail.trim());
  const isUpdating = workspaceStore.isLoading;
  const canConfirm = isConfirmed && isEmailValid && !isUpdating;

  const handleClose = () => {
    if (!isUpdating && !isLoading) {
      setNewOwnerEmail('');
      setIsConfirmed(false);
      workspaceStore.clearError();
      onClose();
    }
  };

  const handleConfirm = () => {
    if (canConfirm && onConfirm) {
      onConfirm(newOwnerEmail);
    }
  };

  const handleEmailChange = (value) => {
    setNewOwnerEmail(value);
  };

  const handleCheckboxChange = () => {
    setIsConfirmed((prev) => !prev);
  };

  // Initialize email field with current owner email when modal opens
  useEffect(() => {
    if (isOpen && workspace) {
      setNewOwnerEmail(currentOwnerEmail);
      setIsConfirmed(false);
    } else if (!isOpen) {
      setNewOwnerEmail('');
      setIsConfirmed(false);
    }
  }, [isOpen, workspace, currentOwnerEmail]);

  // Show toast notifications based on store state
  useEffect(() => {
    // Only process when loading transitions from true to false
    if (previousLoadingRef.current && !isUpdating && isOpen) {
      // Check for success first
      if (workspaceStore.selectedWorkspace && !workspaceStore.error) {
        // Success: owner was changed
        // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
        const workspaceName = workspaceStore.selectedWorkspace.workspaceName || workspaceStore.selectedWorkspace.workspace_name || workspaceStore.selectedWorkspace.tenantName || workspaceStore.selectedWorkspace.tenant_name || workspaceStore.selectedWorkspace.name || 'Workspace';
        toast.success({
          title: 'Owner updated',
          message: `The workspace owner has been successfully changed.`,
          canClose: true
        });
        // Reset form and close modal
        setNewOwnerEmail('');
        setIsConfirmed(false);
        workspaceStore.clearError();
        onClose();
      } 
      // Check for error
      else if (workspaceStore.error) {
        // Error: show error toast
        const errorMessage = workspaceStore.errorMessage || 'An error occurred while changing workspace owner';
        toast.error({
          message: errorMessage,
          canClose: true
        });
      }
    }
    previousLoadingRef.current = isUpdating;
  }, [isUpdating, workspaceStore.selectedWorkspace, workspaceStore.error, workspaceStore.errorMessage, isOpen, toast, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={modalWidth} size="medium">
      <Modal.Header>
        <Modal.Header.Title/>      
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <MessageText>
            <Typography category="title" size="medium" weight="bold">
              {title}
            </Typography>
            <Typography category="body" size="medium" weight="regular" color="body">
              {message}
            </Typography>
            <CheckboxContainer onClick={handleCheckboxChange}>
              <CheckboxHitArea
                role="presentation"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={isConfirmed}
                  onChange={handleCheckboxChange}
                  size="small"
                />
              </CheckboxHitArea>
              <Typography category="body" size="small" weight="regular" color="body">
                I confirm that I want to change the workspace owner.
              </Typography>
            </CheckboxContainer>
            <FieldContainer>
              <TextField
                label="New owner email"
                onChange={handleEmailChange}
                size="small"
                placeholder="eg: newowner@company.com"
                required={true}
                value={newOwnerEmail}
              />
            </FieldContainer>
          </MessageText>
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="center">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isUpdating || isLoading}
        />
        <Modal.Button
          label={isUpdating ? "Changing..." : "Save Changes"}
          onClick={handleConfirm}
          type="error"
          disabled={!canConfirm || isUpdating}
          isLoading={isUpdating}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default observer(ChangeOwnerModal);

